import { Deadline } from './deadline';
import { Refusal, Withheld } from './refusal';

/**
 * What a request that met an ended session becomes once the session is renewed (the family's replay
 * classes): a `read` is sent again; a `write` is sent again when the session was renewed while it
 * waited; `false`, a destructive or irreversible action, never is.
 */
export type Replay = 'read' | 'write' | false;

/** The page's session (`@beyond-js/ui`'s `Session`): resolves with whether to send a request again */
export interface Keeper {
	lost(lost: { reason?: string | null; replay?: Replay }): Promise<boolean>;
}

/** How one request treats an answer that says the session ended */
export interface Calling {
	/** Its replay class; by default `read` for a GET and `write` for a change */
	replay?: Replay;
	/**
	 * Who answers a refusal about the session: `session` (the default) gives it to the page's session;
	 * `caller` leaves it to the caller, which ends or reads the session on purpose (signing out here,
	 * the session's own read); `token` leaves a bare `UNAUTHENTICATED` to the caller, because the request
	 * carries a credential of its own that the answer may be about
	 */
	ended?: 'session' | 'caller' | 'token';
}

/**
 * The versioned transport to the Accounts service.
 *
 * It is the only place in this application that knows a URL, a method or an error envelope. Everything
 * above it works in terms of the domain, so moving to another protocol version means changing this
 * class and its tests, not every screen.
 *
 * The session lives in a host-only cookie the browser sends, so requests are made with credentials and
 * this class never holds a credential of its own — there is nothing here for a cross-site script to
 * read.
 *
 * Every request ends within `wait`, its body included, which is longer than the service's own worst
 * case so a person sees the service's precise answer whenever there is one. A request that did not end
 * in an answer is never a refusal: a read becomes `UNREACHABLE`, and a change becomes `UNANSWERED`,
 * because the service may have made it before the answer was lost.
 *
 * An answer that says the session ended (`UNAUTHENTICATED`, `SESSION_EXPIRED`, `SESSION_REVOKED`,
 * `ACCOUNT_SUSPENDED`) while someone is signed in goes to the page's session (`session`), which renews
 * it or asks the person once, for every product alike. The request is sent once more when the session
 * says so; otherwise it is rejected as `Withheld`, which no screen shows as an error. Such a request was
 * not carried out, so sending it again cannot make it twice.
 */
export class Client {
	static PROTOCOL = 'beyond-accounts/1';
	/** How long one request may take (ms), read when a client is created */
	static WAIT = 20000;

	#origin: string;
	#fetcher: typeof fetch;
	#wait: number;
	#session: Keeper | null = null;

	/**
	 * @param fetcher Injected so tests never reach the network. It is bound to the global on purpose:
	 * a browser refuses `fetch` called as a method of anything but the window.
	 * @param wait Milliseconds one request may take, its body included
	 */
	constructor(origin: string, fetcher: typeof fetch = (...args) => globalThis.fetch(...args), wait = Client.WAIT) {
		this.#origin = origin.replace(/\/$/, '');
		this.#fetcher = fetcher;
		this.#wait = wait;
	}

	/** The page's session while someone is signed in, which answers an ended session; null otherwise */
	get session(): Keeper | null {
		return this.#session;
	}

	set session(value: Keeper | null) {
		this.#session = value;
	}

	/**
	 * @throws Refusal for any answer that is not a success, `UNREACHABLE` for a read that got no
	 * answer and `UNANSWERED` for a change that got none; `Withheld` for a request that met an ended
	 * session and is not sent again
	 */
	async call<T>(method: string, path: string, body?: unknown, calling: Calling = {}): Promise<T> {
		try {
			return await this.#send<T>(method, path, body);
		} catch (error) {
			const keeper = this.#session;
			if (!keeper || !Refusal.is(error) || !Client.#lapsed(error, calling)) throw error;
			const replay = calling.replay ?? (method === 'GET' ? 'read' : 'write');
			const again = await keeper.lost({ reason: error.reason, replay });
			if (!again) throw new Withheld(error, replay === false);
			// Once only: a request refused again right after a renewal is not chased in a loop
			return this.#send<T>(method, path, body).catch(late => {
				throw Refusal.is(late) && Client.#lapsed(late, calling) ? new Withheld(late, replay === false) : late;
			});
		}
	}

	/** Whether a refusal is the page's session's to answer, for this request */
	static #lapsed(refusal: Refusal, { ended = 'session' }: Calling): boolean {
		if (ended === 'caller' || !refusal.lapsed) return false;
		return !(ended === 'token' && refusal.code === 'UNAUTHENTICATED');
	}

	async #send<T>(method: string, path: string, body?: unknown): Promise<T> {
		const deadline = new Deadline(this.#wait);
		const change = method !== 'GET';
		// A picture travels as itself, with its own type (an organization's logo)
		const raw = typeof Blob !== 'undefined' && body instanceof Blob;
		let response: Response;
		let text: string;
		try {
			response = await deadline.race(
				this.#fetcher(`${this.#origin}${path}`, {
					method,
					credentials: 'include',
					headers: body === undefined ? { accept: 'application/json' } : { accept: 'application/json', 'content-type': raw ? (body as Blob).type : 'application/json' },
					body: body === undefined ? undefined : raw ? (body as Blob) : JSON.stringify(body),
					signal: deadline.signal
				})
			);
			text = response.status === 204 ? '' : await deadline.race(response.text());
		} catch (cause) {
			const details = { cause: String(cause), timeout: deadline.passed };
			// A change that ran out of time may have been made before the answer was lost. One that failed
			// before any answer could start — the connection was refused or dropped at once — was not sent,
			// and saying "it may have been made" would send the person to check something that did not
			// happen (ACC-13)
			if (change && deadline.passed) throw new Refusal('UNANSWERED', 'The service did not answer; the change may have been made', 0, details);
			if (change) throw new Refusal('UNSENT', 'The service could not be reached, so the change was not sent', 0, details);
			throw new Refusal('UNREACHABLE', 'The service could not be reached', 0, details);
		} finally {
			deadline.clear();
		}

		if (response.status === 204) return undefined as T;

		let parsed: unknown;
		try {
			parsed = text ? JSON.parse(text) : undefined;
		} catch {
			throw new Refusal('UNREACHABLE', 'The service answered something unreadable', response.status);
		}

		if (!response.ok) {
			const error = (parsed as { error?: { code?: string; message?: string; details?: Record<string, unknown>; request?: string; at?: string } })?.error;
			const retry = Number(response.headers?.get?.('retry-after'));
			const trace = {
				request: error?.request ?? response.headers?.get?.('x-request-id') ?? undefined,
				at: error?.at,
				retry: Number.isFinite(retry) && retry > 0 ? retry : undefined
			};
			throw new Refusal(error?.code ?? 'INTERNAL', error?.message ?? 'The request failed', response.status, error?.details, trace);
		}
		return parsed as T;
	}

	get<T>(path: string, calling?: Calling): Promise<T> {
		return this.call<T>('GET', path, undefined, calling);
	}
	post<T>(path: string, body?: unknown, calling?: Calling): Promise<T> {
		return this.call<T>('POST', path, body ?? {}, calling);
	}
	patch<T>(path: string, body: unknown, calling?: Calling): Promise<T> {
		return this.call<T>('PATCH', path, body, calling);
	}
	/** Sends a file as the request's body; never sent again after a renewal */
	upload<T>(method: string, path: string, file: Blob, calling: Calling = {}): Promise<T> {
		return this.call<T>(method, path, file, { replay: false, ...calling });
	}
	delete<T>(path: string, calling?: Calling): Promise<T> {
		return this.call<T>('DELETE', path, undefined, calling);
	}
}
